import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { CompactType, DisplayGrid, GridsterConfig, GridsterItem, GridsterItemComponent, GridType } from 'angular-gridster2';
import { cloneDeep } from 'lodash-es';
import { LandingPageTile } from '../shared/tiles/home-tile.model';
import { StateService } from '../shared/state/state.service';
import { AppMessageService } from '../shared/app-message/app-message.service';
import { ChangeDetectorRef } from '@angular/core';
import { LandingControllerComponent } from './landing-controller/landing-controller.component';
import { LandingPageService } from './landing-page.service';

@Component({
  selector: 'qm-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPageComponent implements OnInit {

  public landingController = LandingControllerComponent;
  public options: GridsterConfig;
  // public dashboard: Array<LandingPageTile> = [];
  public hoverTileId: string;

  private restorableDashboard: Array<LandingPageTile>;
  private maxCols = 32;
  private maxRows = 20;

  constructor(private appMessageService: AppMessageService,
              private changeDetector: ChangeDetectorRef,
              private stateService: StateService,
              private landingPageService: LandingPageService) {
  }

  private static getComponentName(): string {
    return 'LandingPage';
  }

  ngOnInit() {
    this.options = {
      gridType: GridType.Fixed,
      compactType: CompactType.CompactLeftAndUp,
      margin: 0,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      mobileBreakpoint: 640,
      itemChangeCallback: () => {
        this.updateState();
      },
      itemResizeCallback: (item: GridsterItem, component: GridsterItemComponent) => {
        this.updateState();
        if (item?.onResize) {
          item.onResize(component.width, component.height);
        }
      },
      itemInitCallback: () => {
        this.updateState();
      },
      itemRemovedCallback: () => {
        this.updateState();
      },
      minCols: 1,
      maxCols: this.maxCols,
      minRows: 1,
      maxRows: this.maxRows,
      maxItemCols: this.maxCols,
      minItemCols: 1,
      maxItemRows: this.maxRows,
      minItemRows: 1,
      maxItemArea: 2500,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      keepFixedHeightInMobile: false,
      keepFixedWidthInMobile: false,
      scrollSensitivity: 10,
      scrollSpeed: this.maxRows,
      enableEmptyCellClick: false,
      enableEmptyCellContextMenu: false,
      enableEmptyCellDrop: false,
      enableEmptyCellDrag: false,
      emptyCellDragMaxCols: 50,
      emptyCellDragMaxRows: 50,
      ignoreMarginInRow: false,
      draggable: {
        dragHandleClass: 'drag-handler',
        ignoreContent: true,
        enabled: true,
      },
      resizable: {
        enabled: true,
      },
      swap: false,
      pushItems: true,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: {north: true, east: true, south: true, west: true},
      pushResizeItems: false,
      displayGrid: DisplayGrid.None,
      disableWindowResize: false,
      disableWarnings: false,
      scrollToNewItems: false
    };
    this.calculateGridDimensions();
    try {
      for (const tile of this.stateService.getPartOfState(LandingPageComponent.getComponentName())) {
        const panel = this.getPanelById(tile.panel);
        panel.cols = tile.cols;
        panel.rows = tile.rows;
        panel.x = tile.x;
        panel.y = tile.y;
        this.landingPageService.dashboard.push(panel);
      }
    } catch (error) {
      this.landingPageService.revertToDefault();
    }
  }

  private calculateGridDimensions(): void {
    const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const viewWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const emInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const gridHeight = viewHeight - 2.8 * emInPixels;
    const gridWidth = viewWidth;
    this.options.fixedColWidth = Math.floor(gridWidth / this.maxCols);
    this.options.fixedRowHeight = Math.floor(gridHeight / this.maxRows);
  }

  private getPanelById(id: string): LandingPageTile {
    return this.landingPageService.getPanelById(id);
  }

  public changedOptions(): void {
    if (this.options.api && this.options.api.optionsChanged) {
      this.options.api.optionsChanged();
    }
  }

  public removeTile($event, item: LandingPageTile): void {
    this.landingPageService.removeTile($event, item);
  }

  public maximizeTile($event, item: LandingPageTile): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.restorableDashboard = cloneDeep(this.landingPageService.dashboard);
    item.maximized = true;
    item.x = 0;
    item.y = 0;
    item.cols = this.maxCols;
    item.rows = this.maxRows;
    item.resizeEnabled = false;
    item.dragEnabled = false;
    this.landingPageService.dashboard = [item];
    console.log(this.landingPageService.dashboard);
    this.options.api.optionsChanged();
  }

  public restoreTile($event, item: LandingPageTile): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.landingPageService.dashboard = cloneDeep(this.restorableDashboard);
    this.options.api.optionsChanged();
  }

  public isTileVisible(itemId: string): boolean {
    return this.landingPageService.isTileVisible(itemId);
  }

  public toggleTileVisible(item: LandingPageTile): void {
    this.landingPageService.toggleTileVisible(item);
  }

  public getDashboard(): Array<LandingPageTile> {
    return this.landingPageService.dashboard;
  }

  @HostListener('window:resize', ['$event'])
  private onResize(event) {
    console.log('Width: ' + event.target.innerWidth);
    this.calculateGridDimensions();
    this.changedOptions();
    // this.options.api.resize();
  }

  private updateState() {
    console.log('Update landing state');
    this.stateService.updateState(LandingPageComponent.getComponentName(), this.landingPageService.dashboard);
  }
}
